import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	Card,
	Checkbox,
	DatePicker,
	Input,
	Modal,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SendOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import faturaService from '../services/faturaService';
import { handleApiError } from '../utils/apiError';

const { Text } = Typography;

const STATUS_COLORS = {
	ABERTA: 'blue',
	PAGA: 'green',
	VENCIDA: 'red',
	CANCELADA: 'default',
	SEM_FATURA: 'default',
};

const STATUS_CONSOLIDADO_COLORS = {
	ELEGIVEL_GERACAO: 'gold',
	ELEGIVEL_COMPLEMENTAR: 'cyan',
	GERADA_NAO_ENVIADA: 'blue',
	ENVIADA_AGUARDANDO: 'orange',
	RECEBIDA: 'green',
	BLOQUEADA: 'default',
};

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
	style: 'currency',
	currency: 'BRL',
});

const formatCurrency = (value) => {
	const numericValue = Number(value ?? 0);
	return Number.isFinite(numericValue) ? moneyFormatter.format(numericValue) : moneyFormatter.format(0);
};

const formatDate = (value) => {
	if (!value) return '-';

	if (typeof value === 'string') {
		const plainDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		if (plainDateMatch) {
			const [, year, month, day] = plainDateMatch;
			return `${day}/${month}/${year}`;
		}
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleDateString('pt-BR');
};

const formatParcelasEnvioAsaas = (record) => {
	const totalParcelas = Number(record?.qtd_parcelas_total || 0);
	if (!Number.isFinite(totalParcelas) || totalParcelas <= 0) return '-';
	return totalParcelas === 1 ? '1 parcela' : `${totalParcelas} parcelas`;
};

const FaturasOperacional = () => {
	const [competenciaOperacional, setCompetenciaOperacional] = useState(dayjs());
	const [operacionalRows, setOperacionalRows] = useState([]);
	const [operacionalLoading, setOperacionalLoading] = useState(false);
	const [filtroAlunoOperacional, setFiltroAlunoOperacional] = useState('');
	const [filtroBloqueadasBusca, setFiltroBloqueadasBusca] = useState('');
	const [somenteBloqueadasComElegivel, setSomenteBloqueadasComElegivel] = useState(false);
	const [somenteBloqueadasComplementar, setSomenteBloqueadasComplementar] = useState(false);
	const [gerandoLote, setGerandoLote] = useState(false);
	const [enviandoLoteAsaas, setEnviandoLoteAsaas] = useState(false);
	const [operacionalAcaoKey, setOperacionalAcaoKey] = useState(null);
	const [selectedGeracaoRowKeys, setSelectedGeracaoRowKeys] = useState([]);
	const [selectedGeracaoRows, setSelectedGeracaoRows] = useState([]);
	const [selectedEnvioRowKeys, setSelectedEnvioRowKeys] = useState([]);
	const [selectedEnvioRows, setSelectedEnvioRows] = useState([]);

	const getOperacionalRowKey = (record) => `${record.id_aluno}-${record.competencia}`;

	const fetchOperacional = async (compDate = competenciaOperacional) => {
		if (!compDate) {
			setOperacionalRows([]);
			setSelectedGeracaoRowKeys([]);
			setSelectedGeracaoRows([]);
			setSelectedEnvioRowKeys([]);
			setSelectedEnvioRows([]);
			return;
		}

		setOperacionalLoading(true);
		try {
			const competencia = compDate.format('YYYY-MM');
			const response = await faturaService.listOperacional(competencia, false);
			setOperacionalRows(Array.isArray(response.data) ? response.data : []);
			setSelectedGeracaoRowKeys([]);
			setSelectedGeracaoRows([]);
			setSelectedEnvioRowKeys([]);
			setSelectedEnvioRows([]);
		} catch (error) {
			handleApiError(error, 'Erro ao buscar visão operacional de faturas');
		} finally {
			setOperacionalLoading(false);
		}
	};

	const abrirModalResultadoGeracaoLote = (data, itensSelecionados) => {
		const resultados = Array.isArray(data?.resultados) ? data.resultados : [];
		const itensPorChave = new Map(
			(itensSelecionados || []).map((row) => [
				`${row.id_aluno}-${row.competencia}`,
				row,
			])
		);

		const resultadoDataSource = resultados.map((item, index) => {
			const chave = `${item.id_aluno}-${item.competencia}`;
			const origem = itensPorChave.get(chave);
			return {
				key: `${chave}-${index}`,
				aluno: origem?.nome_aluno || `ID ${item.id_aluno}`,
				competencia: item.competencia || '-',
				sucesso: Boolean(item.sucesso),
				mensagem: item.mensagem || '-',
			};
		});

		Modal.info({
			title: 'Resultado da geração em lote',
			width: 900,
			content: (
				<Space direction="vertical" style={{ width: '100%' }}>
					<Space>
						<span>Total processado: {data?.total ?? 0}</span>
						<span>Sucessos: {data?.sucessos ?? 0}</span>
						<span>Falhas: {data?.falhas ?? 0}</span>
					</Space>
					<Table
						size="small"
						pagination={{ pageSize: 8, showSizeChanger: false }}
						dataSource={resultadoDataSource}
						columns={[
							{ title: 'Aluno', dataIndex: 'aluno', key: 'aluno' },
							{ title: 'Competência', dataIndex: 'competencia', key: 'competencia', width: 130, align: 'center' },
							{
								title: 'Resultado',
								dataIndex: 'sucesso',
								key: 'sucesso',
								width: 120,
								align: 'center',
								render: (sucesso) => <Tag color={sucesso ? 'green' : 'red'}>{sucesso ? 'SUCESSO' : 'FALHA'}</Tag>,
							},
							{ title: 'Mensagem', dataIndex: 'mensagem', key: 'mensagem' },
						]}
						locale={{ emptyText: 'Sem resultados detalhados' }}
					/>
				</Space>
			),
		});
	};

	const abrirModalResultadoEnvioLote = (data, itensSelecionados) => {
		const resultados = Array.isArray(data?.resultados) ? data.resultados : [];
		const itensPorChave = new Map(
			(itensSelecionados || []).map((row) => [
				`${row.id_aluno}-${row.competencia}`,
				row,
			])
		);

		const resultadoDataSource = resultados.map((item, index) => {
			const chave = `${item.id_aluno}-${item.competencia}`;
			const origem = itensPorChave.get(chave);
			return {
				key: `${chave}-${index}`,
				aluno: origem?.nome_aluno || `ID ${item.id_aluno}`,
				competencia: item.competencia || '-',
				sucesso: Boolean(item.sucesso),
				mensagem: [item.mensagem, item.debug_reason].filter(Boolean).join(' | '),
			};
		});

		Modal.info({
			title: 'Resultado do envio em lote ao ASAAS',
			width: 900,
			content: (
				<Space direction="vertical" style={{ width: '100%' }}>
					<Space>
						<span>Total processado: {data?.total ?? 0}</span>
						<span>Sucessos: {data?.sucessos ?? 0}</span>
						<span>Falhas: {data?.falhas ?? 0}</span>
					</Space>
					<Table
						size="small"
						pagination={{ pageSize: 8, showSizeChanger: false }}
						dataSource={resultadoDataSource}
						columns={[
							{ title: 'Aluno', dataIndex: 'aluno', key: 'aluno' },
							{ title: 'Competência', dataIndex: 'competencia', key: 'competencia', width: 130, align: 'center' },
							{
								title: 'Resultado',
								dataIndex: 'sucesso',
								key: 'sucesso',
								width: 120,
								align: 'center',
								render: (sucesso) => <Tag color={sucesso ? 'green' : 'red'}>{sucesso ? 'SUCESSO' : 'FALHA'}</Tag>,
							},
							{ title: 'Mensagem', dataIndex: 'mensagem', key: 'mensagem' },
						]}
						locale={{ emptyText: 'Sem resultados detalhados' }}
					/>
				</Space>
			),
		});
	};

	const onGerarLote = async () => {
		if (selectedGeracaoRows.length === 0) return;

		const itens = selectedGeracaoRows.map((row) => ({
			id_aluno: row.id_aluno,
			competencia: row.competencia,
		}));

		setGerandoLote(true);
		try {
			const response = await faturaService.gerarLote({ itens });
			const data = response.data || {};
			abrirModalResultadoGeracaoLote(data, selectedGeracaoRows);
			await fetchOperacional(competenciaOperacional);
		} catch (error) {
			handleApiError(error, 'Erro ao gerar faturas em lote');
		} finally {
			setGerandoLote(false);
		}
	};

	const onEnviarAsaasLote = async () => {
		if (selectedEnvioRows.length === 0) return;

		const itens = selectedEnvioRows.map((row) => ({
			id_aluno: row.id_aluno,
			competencia: row.competencia,
		}));

		const linhasSelecionadas = [...selectedEnvioRows];

		Modal.confirm({
			title: `Deseja enviar ${itens.length} faturas selecionadas ao Asaas?`,
			content: 'Essa ação criará cobranças externas para as faturas elegíveis.',
			okText: 'Confirmar',
			cancelText: 'Cancelar',
			onOk: async () => {
				setEnviandoLoteAsaas(true);
				try {
					const response = await faturaService.enviarLoteAsaas({ itens });
					const data = response.data || {};
					abrirModalResultadoEnvioLote(data, linhasSelecionadas);
					await fetchOperacional(competenciaOperacional);
				} catch (error) {
					handleApiError(error, 'Erro ao enviar faturas em lote ao ASAAS');
				} finally {
					setEnviandoLoteAsaas(false);
				}
			},
		});
	};

	const sincronizarAsaasLinha = async (record) => {
		const rowKey = getOperacionalRowKey(record);
		setOperacionalAcaoKey(rowKey);
		try {
			const response = await faturaService.sincronizarAsaas({
				itens: [{ id_aluno: record.id_aluno, competencia: record.competencia }],
			});
			const item = response.data?.resultados?.[0];
			if (item?.sucesso) {
				message.success(item.mensagem || 'Status sincronizado com o ASAAS');
			} else {
				message.warning(item?.mensagem || 'Falha ao sincronizar no ASAAS');
			}
			await fetchOperacional(competenciaOperacional);
		} catch (error) {
			handleApiError(error, 'Erro ao sincronizar status no ASAAS');
		} finally {
			setOperacionalAcaoKey(null);
		}
	};

	const termoAluno = (filtroAlunoOperacional || '').trim().toLowerCase();

	const rowsFiltradasAluno = useMemo(
		() =>
			operacionalRows.filter((row) => {
				if (!termoAluno) return true;
				const nome = (row?.nome_aluno || '').toLowerCase();
				const id = String(row?.id_aluno || '');
				return nome.includes(termoAluno) || id.includes(termoAluno);
			}),
		[operacionalRows, termoAluno]
	);

	const rowsElegiveisGeracao = useMemo(
		() => rowsFiltradasAluno.filter((row) => row.is_elegivel_geracao),
		[rowsFiltradasAluno]
	);

	const rowsElegiveisEnvio = useMemo(
		() => rowsFiltradasAluno.filter((row) => row.is_elegivel_envio_asaas),
		[rowsFiltradasAluno]
	);

	const rowsBloqueadasVencimento = useMemo(
		() => rowsFiltradasAluno.filter((row) => row.vencimento_expirado),
		[rowsFiltradasAluno]
	);

	const termoBloqueadas = (filtroBloqueadasBusca || '').trim().toLowerCase();

	const rowsBloqueadasFiltradas = useMemo(
		() =>
			rowsBloqueadasVencimento.filter((row) => {
				if (termoBloqueadas) {
					const nomeAluno = (row?.nome_aluno || '').toLowerCase();
					const nomeResponsavel = (row?.responsavel_nome || '').toLowerCase();
					const idAluno = String(row?.id_aluno || '');
					const matchBusca =
						nomeAluno.includes(termoBloqueadas) ||
						nomeResponsavel.includes(termoBloqueadas) ||
						idAluno.includes(termoBloqueadas);
					if (!matchBusca) return false;
				}

				if (somenteBloqueadasComElegivel && Number(row?.valor_total_elegivel || 0) <= 0) {
					return false;
				}

				if (
					somenteBloqueadasComplementar &&
					row?.status_consolidado !== 'ELEGIVEL_COMPLEMENTAR'
				) {
					return false;
				}

				return true;
			}),
		[
			rowsBloqueadasVencimento,
			termoBloqueadas,
			somenteBloqueadasComElegivel,
			somenteBloqueadasComplementar,
		]
	);

	useEffect(() => {
		fetchOperacional(dayjs());
	}, []);

	const operacionalColumns = [
		{
			title: 'Aluno',
			key: 'aluno',
			render: (_, record) => record.nome_aluno || record.id_aluno,
		},
		{
			title: 'Responsável',
			dataIndex: 'responsavel_nome',
			key: 'responsavel_nome',
			render: (value) => value || '-',
		},
		{
			title: 'Competência',
			dataIndex: 'competencia',
			key: 'competencia',
			width: 120,
			align: 'center',
		},
		{
			title: 'Valor Total',
			dataIndex: 'valor_total_competencia',
			key: 'valor_total_competencia',
			render: formatCurrency,
			width: 140,
			align: 'right',
		},
		{
			title: 'Valor Elegível',
			dataIndex: 'valor_total_elegivel',
			key: 'valor_total_elegivel',
			render: formatCurrency,
			width: 140,
			align: 'right',
		},
		{
			title: 'Parcelas',
			key: 'parcelas',
			width: 120,
			align: 'center',
			render: (_, record) => `${record.qtd_parcelas_elegiveis}/${record.qtd_parcelas_total}`,
		},
		{
			title: 'Fatura',
			dataIndex: 'status_fatura',
			key: 'status_fatura',
			width: 150,
			align: 'center',
			render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status || '-'}</Tag>,
		},
		{
			title: 'Asaas',
			dataIndex: 'status_asaas',
			key: 'status_asaas',
			width: 150,
			align: 'center',
			render: (status) => status || '-',
		},
		{
			title: 'Consolidado',
			dataIndex: 'status_consolidado',
			key: 'status_consolidado',
			width: 170,
			align: 'center',
			render: (status) => <Tag color={STATUS_CONSOLIDADO_COLORS[status] || 'default'}>{status || '-'}</Tag>,
		},
		{
			title: 'Vencimento',
			dataIndex: 'data_vencimento',
			key: 'data_vencimento',
			width: 130,
			align: 'center',
			render: formatDate,
		},
		{
			title: 'Ações Asaas',
			key: 'acoes_asaas',
			width: 90,
			align: 'center',
			render: (_, record) => {
				if (!record.has_cobranca_historica) {
					return '-';
				}

				const rowKey = getOperacionalRowKey(record);
				const isLoading = operacionalAcaoKey === rowKey;

				return (
					<Space>
						<Tooltip title="Sincronizar">
							<Button
								size="small"
								icon={<SyncOutlined />}
								onClick={() => sincronizarAsaasLinha(record)}
								loading={isLoading}
							/>
						</Tooltip>
					</Space>
				);
			},
		},
	];

	const operacionalColumnsEnvioAsaas = operacionalColumns
		.filter((column) => column.key !== 'valor_total_elegivel')
		.map((column) => {
			if (column.key === 'parcelas') {
				return {
					...column,
					render: (_, record) => formatParcelasEnvioAsaas(record),
				};
			}

			if (column.key !== 'valor_total_competencia') {
				return column;
			}

			return {
				...column,
				title: 'Valor Envio ASAAS',
				key: 'valor_envio_asaas',
			};
		});

	return (
		<div>
			<Card title="Operacional por Competência" style={{ marginBottom: 16 }}>
				<Space wrap style={{ marginBottom: 12 }}>
					<DatePicker
						picker="month"
						format="MM/YYYY"
						value={competenciaOperacional}
						onChange={(value) => setCompetenciaOperacional(value)}
					/>
					<Tooltip title="Sincronizar lista operacional">
						<Button
							icon={<ReloadOutlined />}
							onClick={() => fetchOperacional(competenciaOperacional)}
							loading={operacionalLoading}
						/>
					</Tooltip>
					<Input
						allowClear
						placeholder="Buscar por aluno (nome ou ID)"
						style={{ width: 320 }}
						value={filtroAlunoOperacional}
						onChange={(event) => setFiltroAlunoOperacional(event.target.value)}
					/>
				</Space>
			</Card>

			<Card
				title="Elegíveis para Geração"
				style={{ marginBottom: 16 }}
				extra={<Text type="secondary">Total elegíveis: {rowsElegiveisGeracao.length}</Text>}
			>
				<Space style={{ marginBottom: 12 }}>
					<Tooltip title={`Gerar faturas em lote (${selectedGeracaoRows.length} selecionadas)`}>
						<span>
							<Button
								type="primary"
								icon={<PlusOutlined />}
								onClick={onGerarLote}
								loading={gerandoLote}
								disabled={selectedGeracaoRows.length === 0}
							>
								Gerar em lote
							</Button>
						</span>
					</Tooltip>
					<Text type="secondary">Selecionadas: {selectedGeracaoRows.length}</Text>
				</Space>

				<Table
					rowKey={(row) => getOperacionalRowKey(row)}
					loading={operacionalLoading}
					dataSource={rowsElegiveisGeracao}
					columns={operacionalColumns}
					rowSelection={{
						selectedRowKeys: selectedGeracaoRowKeys,
						onChange: (keys, rows) => {
							setSelectedGeracaoRowKeys(keys);
							setSelectedGeracaoRows(rows);
						},
					}}
					pagination={{ pageSize: 10, showSizeChanger: true }}
					locale={{ emptyText: 'Nenhuma fatura elegível para geração nesta competência' }}
				/>
			</Card>

			<Card
				title="Elegíveis para Envio ao ASAAS"
				extra={<Text type="secondary">Total elegíveis: {rowsElegiveisEnvio.length}</Text>}
			>
				<Space style={{ marginBottom: 12 }}>
					<Tooltip title={`Enviar ao Asaas em lote (${selectedEnvioRows.length} selecionadas)`}>
						<span>
							<Button
								icon={<SendOutlined />}
								onClick={onEnviarAsaasLote}
								loading={enviandoLoteAsaas}
								disabled={selectedEnvioRows.length === 0}
							>
								Enviar em lote ao ASAAS
							</Button>
						</span>
					</Tooltip>
					<Text type="secondary">Selecionadas: {selectedEnvioRows.length}</Text>
				</Space>

				<Table
					rowKey={(row) => getOperacionalRowKey(row)}
					loading={operacionalLoading}
					dataSource={rowsElegiveisEnvio}
					columns={operacionalColumnsEnvioAsaas}
					rowSelection={{
						selectedRowKeys: selectedEnvioRowKeys,
						onChange: (keys, rows) => {
							setSelectedEnvioRowKeys(keys);
							setSelectedEnvioRows(rows);
						},
					}}
					pagination={{ pageSize: 10, showSizeChanger: true }}
					locale={{ emptyText: 'Nenhuma fatura elegível para envio ao ASAAS nesta competência' }}
				/>
			</Card>

			<Card
				title="Bloqueadas por Vencimento"
				style={{ marginTop: 16 }}
				extra={<Text type="secondary">Total bloqueadas: {rowsBloqueadasFiltradas.length}</Text>}
			>
				<Space wrap style={{ marginBottom: 12 }}>
					<Input
						allowClear
						placeholder="Filtrar bloqueadas por aluno/responsável"
						style={{ width: 320 }}
						value={filtroBloqueadasBusca}
						onChange={(event) => setFiltroBloqueadasBusca(event.target.value)}
					/>
					<Checkbox
						checked={somenteBloqueadasComElegivel}
						onChange={(event) => setSomenteBloqueadasComElegivel(event.target.checked)}
					>
						Somente valor elegível {'>'} 0
					</Checkbox>
					<Checkbox
						checked={somenteBloqueadasComplementar}
						onChange={(event) => setSomenteBloqueadasComplementar(event.target.checked)}
					>
						Somente ELEGIVEL_COMPLEMENTAR
					</Checkbox>
				</Space>

				<Table
					rowKey={(row) => `bloqueada-${getOperacionalRowKey(row)}`}
					loading={operacionalLoading}
					dataSource={rowsBloqueadasFiltradas}
					columns={[
						...operacionalColumns,
						{
							title: 'Motivo',
							key: 'motivo_bloqueio',
							render: () => <Tag color="red">Vencimento expirado</Tag>,
						},
					]}
					pagination={{ pageSize: 10, showSizeChanger: true }}
					locale={{ emptyText: 'Sem bloqueios por vencimento nesta competência' }}
				/>
			</Card>
		</div>
	);
};

export default FaturasOperacional;
